'use client';

import { memo, useState } from 'react';
import { Element } from '@/data/elements';
import { Badge } from '@/components/ui/badge';
import { useFavorites } from '@/contexts/FavoritesContext';
import { Heart, Mountain } from 'lucide-react';
import { naturalFormsData, MineralSprite } from '@/data/natural-forms-data';
import { useTranslations, useLocale } from 'next-intl';
import { getLocalizedElementData } from '@/data/elements-en';
import { naturalFormsDataEn } from '@/data/natural-forms-data-en';

function ElementSprite({ sprite, color }: { sprite: MineralSprite; color: string }) {
  const xPct = sprite.cols > 1 ? (sprite.col / (sprite.cols - 1)) * 100 : 0;
  const yPct = sprite.rows > 1 ? (sprite.row / (sprite.rows - 1)) * 100 : 0;
  return (
    <div
      style={{
        width: 96,
        height: 96,
        position: 'relative',
        background: '#080f1a',
        borderRadius: 8,
        overflow: 'hidden',
        border: `1px solid ${color}40`,
        flexShrink: 0,
      }}
    >
      <div
        style={{
          position: 'absolute',
          inset: 0,
          backgroundImage: `url(/minerals/${sprite.sheet}.png)`,
          backgroundSize: `${sprite.cols * 100}% ${sprite.rows * 100}%`,
          backgroundPosition: `${xPct}% ${yPct}%`,
          backgroundRepeat: 'no-repeat',
        }}
      />
      <div
        style={{
          position: 'absolute',
          inset: 0,
          background: 'radial-gradient(ellipse at center, transparent 55%, rgba(8,15,26,0.6) 100%)',
        }}
      />
      <span
        style={{
          position: 'absolute',
          bottom: 4,
          right: 6,
          fontSize: 9,
          color,
          textShadow: `0 0 6px ${color}`,
          fontWeight: 500,
        }}
      >
        {sprite.label}
      </span>
    </div>
  );
}

interface ElementCardProps {
  element: Element;
  onClick?: () => void;
}

export const ElementCard = memo(function ElementCard({ element, onClick }: ElementCardProps) {
  const [isHovered, setIsHovered] = useState(false);
  const { isFavorite, toggleFavorite } = useFavorites();
  const favorite = isFavorite(element.id.toString());
  const naturalForm = naturalFormsData[element.atomicNumber];
  const t = useTranslations('Element');
  const locale = useLocale();
  const localizedData = getLocalizedElementData(element.atomicNumber, locale);
  const displayName = localizedData?.name ?? element.name;
  const naturalFormEn = locale === 'en' ? naturalFormsDataEn[element.atomicNumber] : null;

  const categoryNames: Record<string, string> = {
    'alkali_metal': t('categoryAlkaliMetal'),
    'alkaline_earth_metal': t('categoryAlkalineEarthMetal'),
    'transition_metal': t('categoryTransitionMetal'),
    'post_transition_metal': t('categoryPostTransitionMetal'),
    'metalloid': t('categoryMetalloid'),
    'nonmetal': t('categoryNonmetal'),
    'halogen': t('categoryHalogen'),
    'noble_gas': t('categoryNobleGas'),
    'lanthanide': t('categoryLanthanide'),
    'actinide': t('categoryActinide'),
  };

  const stateLabels: Record<string, string> = {
    'solid': t('stateSolid'),
    'liquid': t('stateLiquid'),
    'gas': t('stateGas'),
  };

  return (
    <div
      onClick={onClick}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      className={`
        relative cursor-pointer rounded-xl overflow-hidden
        transition-all duration-300 ease-out
        ${isHovered ? 'scale-105 shadow-2xl' : 'scale-100 shadow-lg'}
        ${isHovered ? 'animate-glow' : ''}
        group
      `}
      style={{
        background: 'linear-gradient(135deg, #0a192f, #112240)',
        border: `2px solid ${element.color}`,
        boxShadow: isHovered ? `0 0 30px ${element.color}50` : `0 0 10px ${element.color}30`,
      }}
    >
      {/* Favorite Button */}
      <button
        onClick={(e) => {
          e.stopPropagation();
          toggleFavorite(element.id.toString());
        }}
        className={`
          absolute top-2 right-2 z-10 p-2 rounded-full
          transition-all duration-300
          ${favorite
            ? 'bg-red-500/20 hover:bg-red-500/30'
            : 'bg-white/10 hover:bg-white/20'
          }
          backdrop-blur-sm
        `}
        aria-label={favorite ? t('removeFromFavorites') : t('addToFavorites')}
      >
        <Heart
          size={16}
          className={`transition-all duration-300 ${
            favorite ? 'fill-red-500 text-red-500' : 'text-white'
          }`}
        />
      </button>

      {/* Header */}
      <div className="dark:bg-black/40 light:bg-white/80 backdrop-blur-sm p-3 border-b dark:border-white/10 light:border-gray-200">
        <div className="grid grid-cols-[auto_1fr] gap-2 items-center">
          <div className="flex flex-col">
            <span
              className="text-sm font-bold dark:text-white light:text-gray-900"
              style={{ textShadow: `0 0 10px ${element.color}` }}
            >
              {element.atomicNumber}
            </span>
            <span className="text-[10px] dark:text-[#ccd6f6] light:text-gray-600">
              {element.atomicWeight.toFixed(3)}
            </span>
          </div>
          <div
            className="text-4xl font-bold text-center"
            style={{
              color: element.color,
              textShadow: `0 0 20px ${element.color}80`
            }}
          >
            {element.symbol}
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="p-4 space-y-3">
        <h3 className="text-base font-semibold dark:text-white light:text-gray-900 text-center">
          {displayName}
        </h3>

        <div className="flex flex-wrap gap-2 justify-center">
          <Badge
            variant="secondary"
            className="text-[10px] px-2 py-0.5"
            style={{
              backgroundColor: `${element.color}20`,
              color: element.color,
              borderColor: element.color
            }}
          >
            {categoryNames[element.category]}
          </Badge>
          <Badge
            variant="outline"
            className="text-[10px] px-2 py-0.5 border-[#64ffda] text-[#64ffda]"
          >
            {stateLabels[element.state]}
          </Badge>
        </div>

        {/* Sprite miniature */}
        {naturalForm?.sprites?.[0] && (
          <div className="flex justify-center">
            <ElementSprite sprite={naturalForm.sprites[0]} color={element.color} />
          </div>
        )}

        {/* Hover info */}
        <div
          className={`
            absolute inset-0 dark:bg-gradient-to-t light:bg-gradient-to-t
            dark:from-black/95 dark:via-black/90 light:from-white/95 light:via-white/90
            to-transparent
            flex flex-col justify-end p-4 space-y-2
            transition-opacity duration-300
            ${isHovered ? 'opacity-100' : 'opacity-0'}
          `}
        >
          <p className="text-xs dark:text-[#64ffda] light:text-teal-600 font-semibold">{t('propertiesHover')}</p>
          {element.density && (
            <p className="text-xs dark:text-white light:text-gray-900">
              {t('densityHover')} <span className="dark:text-[#82aaff] light:text-blue-600">{element.density} g/cm³</span>
            </p>
          )}
          {element.meltingPoint && (
            <p className="text-xs dark:text-white light:text-gray-900">
              {t('meltingPointHover')} <span className="dark:text-[#f78c6c] light:text-orange-600">{element.meltingPoint} K</span>
            </p>
          )}
          {element.electronegativity && (
            <p className="text-xs dark:text-white light:text-gray-900">
              {t('electronegativityHover')} <span className="dark:text-[#c792ea] light:text-purple-600">{element.electronegativity}</span>
            </p>
          )}
          {naturalForm && naturalForm.mainMinerals.length > 0 && (
            <p className="text-xs dark:text-white light:text-gray-900 flex items-center gap-1">
              <Mountain size={11} className="text-[#64ffda]" />
              <span className="dark:text-[#a8b2d1] light:text-gray-600 text-[10px]">
                {naturalFormEn?.mainMinerals[0]?.name ?? naturalForm.mainMinerals[0].name}
                {naturalForm.mainMinerals[1] ? `, ${naturalFormEn?.mainMinerals[1]?.name ?? naturalForm.mainMinerals[1].name}` : ''}
              </span>
            </p>
          )}
          <p className="text-[10px] dark:text-[#64ffda] light:text-teal-600 mt-2">{t('clickForDetails')}</p>
        </div>
      </div>

      {/* Mineral indicator badge */}
      {naturalForm && (
        <div
          className="absolute bottom-2 left-2 flex items-center gap-1 px-1.5 py-0.5 rounded-full"
          style={{ backgroundColor: `${element.color}20`, border: `1px solid ${element.color}40` }}
          title={naturalForm.mainMinerals[0]?.name ?? t('mineral')}
        >
          <Mountain size={10} style={{ color: element.color }} />
          <span className="text-[9px] font-medium" style={{ color: element.color }}>{t('mineral')}</span>
        </div>
      )}

      {/* Corner decoration */}
      <div
        className="absolute top-0 right-0 w-16 h-16 opacity-10"
        style={{
          background: `radial-gradient(circle at top right, ${element.color}, transparent 70%)`,
        }}
      />
    </div>
  );
});
